import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Sync from "@mui/icons-material/Sync";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { Divider, IconButton, Stack, Switch, Tooltip, Typography } from "@mui/material";
import axios, { type AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useBlocker, useNavigate } from "react-router-dom";

import apiRoutes from "../../../constants/api-routes";
import messageLevels from "../../../constants/message-levels";
import messages from "../../../constants/messages";
import pages from "../../../constants/pages";
import validateDomain from "../../../helpers/validateDomain";
import useAuthHeader from "../../../hooks/useAuthHeader";
import useAuthorization from "../../../hooks/useAuthorization";
import useCoAppNavigation from "../../../hooks/useCoAppNavigation";
import useDynamicContainerHeight from "../../../hooks/useDynamicContainerHeight";
import useToastAlert from "../../../hooks/useToastAlert";
import { selectUser } from "../../../redux/userSlice";
import {
	selectOrganizationIdentityProvider,
	selectOrganizationSsoAndScim,
	setOrganizationSsoAndScim
} from "../../../redux/userSlice";
import CoAppMultiSelect from "../../global/components/inputs/CoAppMultiSelect";
import CoAppConfirmationModal from "../../global/components/modals/CoAppConfirmationModal";
import CoAppDestructiveConfirmationModal from "../../global/components/modals/CoAppDestructiveConfirmationModal";
import CoAppKillswitchModal from "../../global/components/modals/CoAppKillswitchModal";
import { CoAppActionButton, CoAppLightTooltip, SsoDomainContainer } from "../../global/styled/global.styled";
import { WorkstationSwitch } from "../workstations/styled/workstation.styled";
import SsoScimWizardPreConfigurationModal from "./scim-wizard/SsoScimWizardPreConfigurationModal";
import {
	OrganizationManagementContainer,
	OrganizationManagementStack
} from "./styled/organization-management.styled";

interface OrganizationDomain {
	name: string;
	organizationId?: number;
}

interface OrganizationMetadata {
	tenantId?: string;
	attributeMappings?: {
		email?: string;
		givenName?: string;
		familyName?: string;
	};
	metadataUrl?: string;
}

interface OrganizationData {
	id: number;
	failedSyncBehavior?: string;
	domains: OrganizationDomain[];
	inUseDomains: OrganizationDomain[];
	forceLoginActive: boolean;
	killSwitchActive: boolean;
	ssoAndScimConfiguredOn?: string | null;
	executionDataEncrypted: boolean;
	firefoxBlockerActive: boolean;
	isOnboarding: boolean;
	ssoMetadata?: OrganizationMetadata;
}

type SimplePatchPayload = Record<string, string | boolean> & { patchType?: string };

const preSsoScimSetupInfo = [
	messages.SCIM_SETUP_MESSAGE_ONE,
	messages.SCIM_SETUP_MESSAGE_TWO,
	messages.SCIM_SETUP_MESSAGE_THREE,
	messages.SCIM_SETUP_MESSAGE_FOUR,
	messages.SCIM_SETUP_MESSAGE_FIVE
];

export default function OrganizationManagement() {
	const isAuthorizedForSsoAndScim = useAuthorization(["MANAGE_SSOSCIM"], false);
	const isAuthorizedForFailedSyncBehavior = useAuthorization(["MANAGE_ORG_FAILED_SYNC_BEHAVIOR"], false);
	const isAuthorizedForForceLogin = useAuthorization(["MANAGE_ORG_FORCELOGIN"], false);
	const isAuthorizedForKillSwitch = useAuthorization(["MANAGE_ORG_KILLSWITCH"], false);
	const isAuthorizedForInUseDomains = useAuthorization(["MANAGE_ORG_DOMAINS"], false);
	const isAuthorizedForExecutionDataEncryption = useAuthorization(["MANAGE_ORG_EXECUTION_DATA_ENCRYPTION"], false);
	const isAuthorizedForFirefoxBlocker = useAuthorization(["MANAGE_ORG_FIREFOX_BLOCKER"], false);
	const isAuthorizedForOnboardingStatus = useAuthorization(["MANAGE_ORG_ONBOARDING"], false);

	const { containerRef, containerHeight } = useDynamicContainerHeight();

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { openScimWizard } = useCoAppNavigation();
	const currentUser = useSelector(selectUser);
	const identityProviderName = useSelector(selectOrganizationIdentityProvider);
	const [organization, setOrganization] = useState<OrganizationData | null>(null);
	const organizationSsoAndScim = useSelector(selectOrganizationSsoAndScim) ?? false;
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false);
	const [disableSsoConfirmationOpen, setDisableSsoConfirmationOpen] = useState<boolean>(false);
	const [ssoAndScimConfiguredDate, setSsoAndScimConfiguredDate] = useState<string | null>(null);
	const [forceLogin, setForceLogin] = useState<boolean>(false);
	const [killSwitch, setKillSwitch] = useState<boolean>(false);
	const [firefoxBlockerActive, setFirefoxBlockerActive] = useState<boolean>(false);
	const [executionDataEncrypted, setExecutionDataEncrypted] = useState<boolean>(false);
	const [companyDomains, setCompanyDomains] = useState<string[]>([]);
	const [domainError, setDomainError] = useState<string>("");
	const [inUseDomains, setInUseDomains] = useState<OrganizationDomain[]>([]);
	const [onboardingStatus, setOnboardingStatus] = useState<boolean>(false);

	const [failedSyncBehavior, setFailedSyncBehavior] = useState<string>("sleep");
	const [forceLoginIsToggled, setForceLoginIsToggled] = useState<boolean>(false);
	const [killSwitchIsToggled, setKillSwitchIsToggled] = useState<boolean>(false);
	const [executionDataEncryptedIsToggled, setExecutionDataEncryptedIsToggled] = useState<boolean>(false);
	const [firefoxBlockerIsToggled, setFirefoxBlockerIsToggled] = useState<boolean>(false);
	const [onboardingStatusIsToggled, setOnboardingStatusIsToggled] = useState<boolean>(false);

	//TODO:: Think about if we want to use this for all mutable settings in org managmement
	const blocker = useBlocker(
		() => {
			return failedSyncBehavior !== (organization?.failedSyncBehavior ?? "sleep");
		}
	);

	const unblock = (e?: Event) => {
		if (e && e.type === "unload") {
			if ("preventDefault" in e) {
				e.preventDefault();
			}
		}
		if (blocker.state === "blocked") {
			blocker.proceed();
		}
	};

	const handleCancelExit = () => {
		blocker?.reset?.();
	};

	const initOrganization = () => {
		if (!currentUser.organizationId) {
			return;
		}
		axios.get<OrganizationData>(apiRoutes.getOrganization(currentUser.organizationId), {
			headers: authHeader
		}).then((res) => {
			const orgData = res.data;
			setOrganization(orgData);
			setInUseDomains(orgData.inUseDomains ?? []);
			setCompanyDomains((orgData.domains ?? []).map(domain => domain.name));
			setForceLogin(orgData.forceLoginActive);
			setKillSwitch(orgData.killSwitchActive);
			setFailedSyncBehavior(orgData.failedSyncBehavior ?? (failedSyncBehavior ?? "sleep"));
			setSsoAndScimConfiguredDate(orgData.ssoAndScimConfiguredOn ?? null);
			setExecutionDataEncrypted(orgData.executionDataEncrypted);
			setFirefoxBlockerActive(orgData.firefoxBlockerActive);
			setOnboardingStatus(orgData.isOnboarding);
		}).catch((error: AxiosError) => {
			console.error("Failed to load organization", error);
		});
	};

	const configureSsoAndScim = () => {
		openScimWizard();
	};

	const confirmSsoAndScimConfiguration = () => {
		setConfirmationDialogOpen(true);
	};

	const handleToggleDisableSsoAndScim = () => {
		setDisableSsoConfirmationOpen(!disableSsoConfirmationOpen);
	};

	const disableSsoAndScim = () => {
		if (!currentUser?.organizationId) {
			return;
		}
		axios.delete(apiRoutes.ssoAndScim(currentUser.organizationId), { headers: authHeader })
			.then(() => {
				navigate(pages.organizationManagement);
				dispatch(setOrganizationSsoAndScim(false));
				handleToggleDisableSsoAndScim();
			})
			.catch(err => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.SSO_AND_SCIM_DISABLED_ERROR_TOAST_MSG);
			});
	};

	const updateOrganizationState = (attribute: string, value: unknown) => {
		switch (attribute) {
			case "failedSyncBehavior":
				setFailedSyncBehavior(value as string);
				break;
			case "killSwitchActive":
				setKillSwitch(value as boolean);
				handleKillSwitchToggle();
				break;
			case "forceLoginActive":
				setForceLogin(value as boolean);
				handleForceLoginSwitchToggle();
				break;
			case "executionDataEncrypted":
				setExecutionDataEncrypted(value as boolean);
				handleExecutionDataEncryptedToggle();
				break;
			case "firefoxBlockerActive":
				setFirefoxBlockerActive(value as boolean);
				handleFirefoxBlockerToggle();
				break;
			case "onboardingStatus":
				setOnboardingStatus(value as boolean);
				handleOnboardingStatusToggle();
				break;
			default:
		}
	};

	const handleSimpleOrganizationPatch = async (newData: SimplePatchPayload, attribute: keyof SimplePatchPayload, errorName: string) => {
		try {
			const data = {
				...newData,
				patchType: "simple"
			};
			if (!currentUser.organizationId) {
				return;
			}
			await axios.patch(apiRoutes.patchOrganization(currentUser.organizationId), data, { headers: authHeader });
			updateOrganizationState(attribute as string, newData[attribute]);
			initOrganization();
		} catch (e) {
			handleToastAlert(messageLevels.ERROR, messages.ORGANIZATION_SIMPLE_PATCH_ERROR_TOAST_MSG(errorName));
		}
	};

	const handleOrganizationDomainsPatch = async (_: unknown, value: string[]) => {
		const lastDomain = value[value.length - 1];

		const domainAlreadyExists = inUseDomains.find(domain => domain.name.toLowerCase() === lastDomain?.toLowerCase() && domain.organizationId !== organization?.id);
		const isValidOrEmpty = validateDomain(lastDomain) || !lastDomain;
		if (isValidOrEmpty && !domainAlreadyExists) {
			const previousDomains = companyDomains;
			try {
				const uniqueDomains = [...new Set(value)];
				setCompanyDomains(uniqueDomains);
				setDomainError("");
				const data = {
					patchType: "domains",
					direction: "all",
					tenantId: organization?.ssoMetadata?.tenantId,
					emailClaimName: organization?.ssoMetadata?.attributeMappings?.email,
					givenNameClaimName: organization?.ssoMetadata?.attributeMappings?.givenName,
					surnameClaimName: organization?.ssoMetadata?.attributeMappings?.familyName,
					domains: uniqueDomains,
					metadataUrl: organization?.ssoMetadata?.metadataUrl,
				};
				if (!currentUser.organizationId) {
					return;
				}
				await axios.patch(apiRoutes.patchOrganization(currentUser.organizationId), data, { headers: authHeader });
				handleToastAlert(messageLevels.SUCCESS, messages.ORGANIZATION_DOMAIN_PATCH_SUCCESS_TOAST_MSG);
			} catch (e) {
				console.log(e);
				setCompanyDomains(previousDomains);
				handleToastAlert(messageLevels.ERROR, messages.ORGANIZATION_DOMAIN_ERROR_TOAST_MSG);
			}
		} else {
			if (domainAlreadyExists) {
				setDomainError(`"${lastDomain}" is already in use`);
			} else {
				setDomainError(`"${lastDomain}" is not a valid domain`);
			}
		}
	};

	const handleForceLoginSwitchToggle = () => {
		setForceLoginIsToggled(!forceLoginIsToggled);
	};

	const handleKillSwitchToggle = () => {
		setKillSwitchIsToggled(!killSwitchIsToggled);
	};

	const handleOnboardingStatusToggle = () => {
		setOnboardingStatusIsToggled(!onboardingStatusIsToggled);
	};

	const handleExecutionDataEncryptedToggle = () => {
		setExecutionDataEncryptedIsToggled(!executionDataEncryptedIsToggled);
	};

	const handleFirefoxBlockerToggle = () => {
		setFirefoxBlockerIsToggled(!firefoxBlockerIsToggled);
	};

	const syncUsers = () => {
		if (!currentUser.organizationId) {
			return;
		}
		axios.post(apiRoutes.sync(currentUser.organizationId), {}, {
			headers: authHeader
		}).then(() => {
			handleToastAlert(messageLevels.SUCCESS, messages.SYNC_ORGANIZATION_STARTED_TOAST_MSG);
		}).catch((err) => {
			handleToastAlert(messageLevels.ERROR, messages.SYNC_ORGANIZATION_FAILED_TO_START_TOAST_MSG);
		});
	};

	useEffect(() => {
		if (isAuthorizedForFailedSyncBehavior || isAuthorizedForInUseDomains || isAuthorizedForForceLogin || 
			isAuthorizedForKillSwitch || isAuthorizedForSsoAndScim || isAuthorizedForFirefoxBlocker || isAuthorizedForOnboardingStatus) {
			initOrganization();
		}
	}, []);

	return (
		<>
			<OrganizationManagementContainer ref={containerRef} height={containerHeight}>
				<OrganizationManagementStack spacing={2}>
					{/* QA: original h1/h3 hierarchy was too heavy and didn't match
					    the rest of the app. Page title now matches the data-grid
					    page-header weight; section titles dropped to a softer
					    subtitle-style row. */}
					<Typography
						sx={{
							fontSize: 26,
							fontWeight: 700,
							letterSpacing: "-0.012em",
							color: "rgba(13, 31, 48, 1)",
							lineHeight: 1.2,
						}}
					>
						{currentUser.organizationName} Organization Settings
					</Typography>
					<Divider sx={{ borderColor: "rgba(13, 31, 48, 0.08)" }} />
					{
						isAuthorizedForSsoAndScim &&
					<>
						<Stack direction="row" spacing={1.25}  sx={{ p: "16px 20px", width: "100%", background: "#FFFFFF", border: "1px solid rgba(13, 31, 48, 0.08)", borderRadius: 3, alignItems:"center" }}>
							{
								organizationSsoAndScim ?
									<>
										<CoAppLightTooltip title="Deactivate SSO + SCIM" placement="right">
											<Switch
												checked={true}
												onChange={handleToggleDisableSsoAndScim}
											/>
										</CoAppLightTooltip>
										<Typography sx={{ fontSize: 15, fontWeight: 400, color: "rgba(13, 31, 48, 0.92)" }}>
											SSO + SCIM
										</Typography>
										<Tooltip title={messages.SSO_AND_SCIM_CONFIGURED_MSG(identityProviderName ?? "")} placement="right" arrow>
											<InfoOutlined sx={{ fontSize: 16, color: "rgba(13, 31, 48, 0.45)", cursor: "help", transition: "color 0.15s", "&:hover": { color: "var(--coapp-text-interactive, #057A6D)" } }} />
										</Tooltip>
										<IconButton onClick={syncUsers} color="primary" size="small">
											<Sync fontSize="small" />
										</IconButton>
									</>
									:
									<>
										<CoAppActionButton
											sx={{ whiteSpace: "nowrap", px: 2.5 }}
											onClick={confirmSsoAndScimConfiguration}
										>
											Configure SSO + SCIM
										</CoAppActionButton>
										<Tooltip title={messages.SSO_AND_SCIM_NOT_CONFIGURED_MSG} placement="right" arrow>
											<InfoOutlined sx={{ fontSize: 16, color: "rgba(13, 31, 48, 0.45)", cursor: "help", transition: "color 0.15s", "&:hover": { color: "var(--coapp-text-interactive, #057A6D)" } }} />
										</Tooltip>
									</>
							}
						</Stack>
						{
							isAuthorizedForInUseDomains && organizationSsoAndScim &&
									<>
										<SsoDomainContainer>
											<CoAppMultiSelect
												label="Company domains"
												options={[]}
												onChange={handleOrganizationDomainsPatch}
												value={companyDomains}
												helperText={domainError !== "" ? domainError : ""}
												error={domainError !== ""}
												isFreeSolo={true}
												disableClearable={true}
											/>
										</SsoDomainContainer>
									</>
						}
					</>
					}
					{
						isAuthorizedForKillSwitch &&
						<>
							<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "16px 20px", background: "#FFFFFF", border: "1px solid rgba(13, 31, 48, 0.08)", borderRadius: 12 }}>
								<div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
									<Typography sx={{ fontSize: 15, fontWeight: 400, color: "rgba(13, 31, 48, 0.92)" }}>
										Organization-Wide Kill Switch
									</Typography>
									{/* The warning triangle now serves as the tooltip trigger on
									    the right (replacing the info "i" icon); the duplicate
									    leading warning icon was removed. */}
									<Tooltip title={killSwitch ? messages.KILLSWITCH_ENABLED_MSG : messages.KILLSWITCH_DISABLED_MSG} placement="right" arrow>
										<WarningAmberRounded sx={{ fontSize: 18, color: killSwitch ? "#B81E15" : "rgba(13, 31, 48, 0.45)", cursor: "help", flexShrink: 0, transition: "color 0.15s", "&:hover": { color: "var(--coapp-text-interactive, #057A6D)" } }} />
									</Tooltip>
								</div>
								<WorkstationSwitch checked={killSwitch} onChange={handleKillSwitchToggle} />
							</div>
						</>
					}
					{
						isAuthorizedForForceLogin &&
						<>
							<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "16px 20px", background: "#FFFFFF", border: "1px solid rgba(13, 31, 48, 0.08)", borderRadius: 12 }}>
								<div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
									<Typography sx={{ fontSize: 15, fontWeight: 400, color: "rgba(13, 31, 48, 0.92)" }}>
										Organization-Wide Force Login
									</Typography>
									<Tooltip title="This forces users to be logged into CoApp while using AcuityLogic." placement="right" arrow>
										<InfoOutlined sx={{ fontSize: 16, color: "rgba(13, 31, 48, 0.45)", cursor: "help", transition: "color 0.15s", "&:hover": { color: "var(--coapp-text-interactive, #057A6D)" } }} />
									</Tooltip>
								</div>
								<Switch checked={forceLogin} onChange={handleForceLoginSwitchToggle} />
							</div>
						</>
					}
					{
						isAuthorizedForExecutionDataEncryption &&
						<>
							<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "16px 20px", background: "#FFFFFF", border: "1px solid rgba(13, 31, 48, 0.08)", borderRadius: 12 }}>
								<div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
									<Typography sx={{ fontSize: 15, fontWeight: 400, color: "rgba(13, 31, 48, 0.92)" }}>
										Organization-Wide Execution Data Encryption
									</Typography>
									<Tooltip title="When enabled, all execution data is encrypted." placement="right" arrow>
										<InfoOutlined sx={{ fontSize: 16, color: "rgba(13, 31, 48, 0.45)", cursor: "help", transition: "color 0.15s", "&:hover": { color: "var(--coapp-text-interactive, #057A6D)" } }} />
									</Tooltip>
								</div>
								<Switch checked={executionDataEncrypted} onChange={handleExecutionDataEncryptedToggle} />
							</div>
						</>
					}
					{
						isAuthorizedForFirefoxBlocker &&
						<>
							<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "16px 20px", background: "#FFFFFF", border: "1px solid rgba(13, 31, 48, 0.08)", borderRadius: 12 }}>
								<div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
									<Typography sx={{ fontSize: 15, fontWeight: 400, color: "rgba(13, 31, 48, 0.92)" }}>
										Organization-Wide Firefox Blocker
									</Typography>
									<Tooltip title="When enabled, all users accessing a configured platform will be blocked from using Firefox as their browser." placement="right" arrow>
										<InfoOutlined sx={{ fontSize: 16, color: "rgba(13, 31, 48, 0.45)", cursor: "help", transition: "color 0.15s", "&:hover": { color: "var(--coapp-text-interactive, #057A6D)" } }} />
									</Tooltip>
								</div>
								<Switch checked={firefoxBlockerActive} onChange={handleFirefoxBlockerToggle} />
							</div>
						</>
					}
					{
						isAuthorizedForFailedSyncBehavior &&
						<>
							<div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", padding: "16px 20px", background: "#FFFFFF", border: "1px solid rgba(13, 31, 48, 0.08)", borderRadius: 12 }}>
								<div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
									<Typography sx={{ fontSize: 15, fontWeight: 400, color: "rgba(13, 31, 48, 0.92)" }}>
										Browser Extension Settings
									</Typography>
									<Tooltip title="How should the Browser Extension react in the event of not being able to connect to CoApp?" placement="right" arrow>
										<InfoOutlined sx={{ fontSize: 16, color: "rgba(13, 31, 48, 0.45)", cursor: "help", transition: "color 0.15s", "&:hover": { color: "var(--coapp-text-interactive, #057A6D)" } }} />
									</Tooltip>
								</div>
								{/* Two paired switches replacing the segmented control.
								    Both derive from the single `failedSyncBehavior`
								    value, so they are inherently mutually exclusive —
								    turning one on flips the other off. Toggling the
								    already-active switch is a no-op so exactly one
								    option stays selected at all times. */}
								<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
									<Typography sx={{ fontSize: 14, fontWeight: 400, color: "rgba(13, 31, 48, 0.92)" }}>
										Immediately go to sleep
									</Typography>
									<Switch
										checked={failedSyncBehavior !== "doNothing"}
										onChange={() => {
											if (failedSyncBehavior !== "doNothing") return;
											handleSimpleOrganizationPatch({ failedSyncBehavior: "sleep" }, "failedSyncBehavior", "Failed Sync Behavior");
										}}
									/>
								</div>
								<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
									<Typography sx={{ fontSize: 14, fontWeight: 400, color: "rgba(13, 31, 48, 0.92)" }}>
										Continue running rules
									</Typography>
									<Switch
										checked={failedSyncBehavior === "doNothing"}
										onChange={() => {
											if (failedSyncBehavior === "doNothing") return;
											handleSimpleOrganizationPatch({ failedSyncBehavior: "doNothing" }, "failedSyncBehavior", "Failed Sync Behavior");
										}}
									/>
								</div>
							</div>
						</>
					}
					{
						isAuthorizedForOnboardingStatus &&
						<>
							<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "16px 20px", background: "#FFFFFF", border: "1px solid rgba(13, 31, 48, 0.08)", borderRadius: 12 }}>
								<div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
									<Typography sx={{ fontSize: 15, fontWeight: 400, color: "rgba(13, 31, 48, 0.92)" }}>
										Onboarding
									</Typography>
									<Tooltip title="When the Organization is in an onboarding state, new workstations introduced will automatically have the kill switch turned ON." placement="right" arrow>
										<InfoOutlined sx={{ fontSize: 16, color: "rgba(13, 31, 48, 0.45)", cursor: "help", transition: "color 0.15s", "&:hover": { color: "var(--coapp-text-interactive, #057A6D)" } }} />
									</Tooltip>
								</div>
								<Switch checked={onboardingStatus} onChange={handleOnboardingStatusToggle} />
							</div>
						</>
					}
				</OrganizationManagementStack>
			</OrganizationManagementContainer>
			<SsoScimWizardPreConfigurationModal
				dialogOpen={confirmationDialogOpen}
				confirmationTitle="Configure SSO + SCIM"
				confirmationMessage="Before you begin:"
				confirmationListItems={preSsoScimSetupInfo}
				confirmClickHandler={configureSsoAndScim}
				cancelClickHandler={() => setConfirmationDialogOpen(false)}
			/>
			<CoAppKillswitchModal
				dialogOpen={killSwitchIsToggled && !killSwitch}
				dialogTitle="Activate organization-wide kill switch?"
				dialogMessage={messages.TOGGLE_ORGANIZATION_WIDE_KILLSWITCH_MSG("disable")}
				confirmClickHandler={() => handleSimpleOrganizationPatch({ killSwitchActive: true }, "killSwitchActive", "Kill Switch")}
				cancelClickHandler={handleKillSwitchToggle}
				actionText="Activate Kill Switch"
			/>
			<CoAppConfirmationModal
				dialogOpen={killSwitchIsToggled && killSwitch}
				dialogTitle="Disable organization-wide kill switch?"
				dialogMessage={messages.TOGGLE_ORGANIZATION_WIDE_KILLSWITCH_MSG("enable")}
				confirmClickHandler={() => handleSimpleOrganizationPatch({ killSwitchActive: false }, "killSwitchActive", "Kill Switch")}
				cancelClickHandler={handleKillSwitchToggle}
				actionText="Disable Kill Switch"
			/>
			<CoAppConfirmationModal
				dialogOpen={forceLoginIsToggled}
				dialogMessage={forceLogin ? messages.ORGANIZTION_WIDE_FORCE_LOGIN_ACTIVATE_MSG : messages.ORGANIZTION_WIDE_FORCE_LOGIN_DEACTIVATE_MSG}
				dialogTitle={`${forceLogin ? "Deactivate" : "Activate"} Organization-Wide Force Login?`}
				confirmClickHandler={() => handleSimpleOrganizationPatch({ forceLoginActive: !forceLogin }, "forceLoginActive", "Force Login")}
				cancelClickHandler={handleForceLoginSwitchToggle}
				actionText={forceLogin ? "Deactivate" : "Activate"}
			/>
			<CoAppConfirmationModal
				dialogOpen={executionDataEncryptedIsToggled}
				dialogTitle="Disable organization-wide execution data encryption?"
				dialogMessage={messages.TOGGLE_ORGANIZATION_WIDE_EXECUTION_DATA_ENCRYPTION_MSG(executionDataEncrypted ? "deactivate" : "activate")}
				confirmClickHandler={() => handleSimpleOrganizationPatch({ executionDataEncrypted: !executionDataEncrypted }, "executionDataEncrypted", "Execution Data Encryption")}
				cancelClickHandler={handleExecutionDataEncryptedToggle}
				actionText={executionDataEncrypted ? "Deactivate" : "Activate"}
			/>
			<CoAppConfirmationModal
				dialogOpen={firefoxBlockerIsToggled}
				dialogTitle={`${firefoxBlockerActive ? "Deactivate" : "Activate"} Organization-Wide Firefox Blocker?`}
				dialogMessage={messages.TOGGLE_ORGANIZATION_WIDE_FIREFOX_BLOCKER_MSG(firefoxBlockerActive ? "deactivate" : "activate")}
				confirmClickHandler={() => handleSimpleOrganizationPatch({ firefoxBlockerActive: !firefoxBlockerActive }, "firefoxBlockerActive", "Firefox Blocker")}
				cancelClickHandler={handleFirefoxBlockerToggle}
				actionText={firefoxBlockerActive ? "Deactivate" : "Activate"}
			/>
			<CoAppConfirmationModal
				dialogOpen={onboardingStatusIsToggled}
				dialogTitle={`${onboardingStatus ? "Deactivate" : "Activate"} Onboarding?`}
				dialogMessage={messages.TOGGLE_ORGANIZATION_WIDE_ONBOARDING_MSG(onboardingStatus ? "deactivate" : "activate")}
				confirmClickHandler={() => handleSimpleOrganizationPatch({ isOnboarding: !onboardingStatus }, "onboardingStatus", "Onboarding")}
				cancelClickHandler={handleOnboardingStatusToggle}
				actionText={onboardingStatus ? "Deactivate" : "Activate"}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={disableSsoConfirmationOpen}
				dialogMessage={messages.DISABLE_SSO_AND_SCIM_MSG}
				dialogTitle="Disable SSO + SCIM?"
				confirmClickHandler={disableSsoAndScim}
				cancelClickHandler={handleToggleDisableSsoAndScim}
				actionText="Disable"
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={blocker.state === "blocked"}
				dialogTitle="Abandon Organization Settings?"
				dialogMessage="There are unsaved changes on this page. If you leave now, any unsaved changes will be lost."
				confirmClickHandler={unblock}
				cancelClickHandler={handleCancelExit}
				actionText="Confirm"
			/>
		</>
	);
}
